import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { Input, Slider } from 'antd';
import { Dropdown, useToast } from '../../components/ui';
import { useNavigate } from 'react-router-dom';
import { useSelectedAccount } from '../../contexts/SelectedAccountContext';
import apiClient from '../../services/api/apiClient';
import PreviewTabs from '../../components/templates/PreviewTabs';
import { BackButton, Button, LoadingButton } from '../../components/ui/Button';
import { PlatformCard, InlineSpinner } from '../../components/ui';

// Platform options - 使用 Apify Transcriber 支援的平台
const platforms = [
  {
    id: 'instagram',
    name: 'Instagram Reels',
    icon: 'mdi:instagram',
    color: '#E1306C',
    description: '15-90 秒短影片',
    pattern: /instagram\.com\/(reel|p)\//i
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: 'ic:baseline-tiktok',
    color: '#000000',
    description: '15-180 秒短影片',
    pattern: /tiktok\.com\/@[\w.-]+\/video\//i
  },
  {
    id: 'facebook',
    name: 'Facebook Reels',
    icon: 'mdi:facebook',
    color: '#1877F2',
    description: '短影片與 Reels',
    pattern: /facebook\.com\/(reel|watch)/i
  }
];

// Script length options
const scriptLengthOptions = [
  { value: '15s', label: '15s', duration: '~3-4 lines' },
  { value: '30s', label: '30s', duration: '~7-8 lines' },
  { value: '60s', label: '60s', duration: '~15-20 lines' },
  { value: '90s', label: '90s', duration: '~25-30 lines' }
];

function CreatorToScriptPage() {
  const navigate = useNavigate();
  const { selectedAccount } = useSelectedAccount();
  const { toast } = useToast();

  // Step management - 從 Step 1 開始（平台選擇）
  const [currentStep, setCurrentStep] = useState(1);

  // Form states
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [topic, setTopic] = useState('');
  const [similarity, setSimilarity] = useState(70);
  const [scriptLength, setScriptLength] = useState('30s');
  const [selectedLanguage, setSelectedLanguage] = useState('zh-TW');
  const [selectedPreset, setSelectedPreset] = useState('default');

  // Transcription states
  const [transcriptionText, setTranscriptionText] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState(null);

  // Generation states
  const [generatedScript, setGeneratedScript] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Recording states
  const [recordedVideo, setRecordedVideo] = useState(null);

  // Subtitle playback states
  const recordedVideoRef = useRef(null);
  const [currentSubtitleText, setCurrentSubtitleText] = useState('');
  const [videoDuration, setVideoDuration] = useState(0);

  // Script editing
  const [isEditingScript, setIsEditingScript] = useState(false);

  // Voice input
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();

      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'zh-TW';

      recognitionInstance.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          }
        }
        if (finalTranscript) {
          setTopic(prev => prev + finalTranscript);
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };

      recognitionInstance.onend = () => {
        setIsRecording(false);
      };

      setRecognition(recognitionInstance);
    }
  }, []);

  const toggleRecording = () => {
    if (!recognition) {
      toast.warning('Your browser does not support voice input.');
      return;
    }

    if (isRecording) {
      recognition.stop();
    } else {
      recognition.start();
      setIsRecording(true);
    }
  };

  // Parse script into subtitle segments
  const scriptSegments = useMemo(() => {
    if (!generatedScript) return [];

    const lines = generatedScript.split('\n');
    const dialogueLines = [];

    // Method 1: Extract quoted dialogue (Chinese quotes)
    for (const line of lines) {
      const chineseQuoteMatch = line.match(/「([^」]+)」/g);
      if (chineseQuoteMatch) {
        chineseQuoteMatch.forEach(match => {
          const text = match.replace(/[「」]/g, '').trim();
          if (text.length > 0) {
            dialogueLines.push({ text });
          }
        });
      }
    }

    // Method 2: Extract quoted dialogue (English quotes)
    if (dialogueLines.length === 0) {
      for (const line of lines) {
        const englishQuoteMatch = line.match(/"([^"]+)"/g);
        if (englishQuoteMatch) {
          englishQuoteMatch.forEach(match => {
            const text = match.replace(/"/g, '').trim();
            if (text.length > 0) {
              dialogueLines.push({ text });
            }
          });
        }
      }
    }

    // Method 3: Fallback - split entire script into meaningful chunks
    if (dialogueLines.length === 0) {
      const cleanLines = lines
        .map(line => line.trim())
        .filter(line => {
          if (!line) return false;
          if (line.startsWith('#')) return false;
          if (line.match(/^\d+-?\d*秒/)) return false;
          if (line.match(/^[\*\-]\s*\*\*/)) return false;
          if (line.length < 5) return false;
          return true;
        })
        .map(line => line.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/^\d+\.\s*/, '').trim())
        .filter(line => line.length > 5);

      cleanLines.forEach(text => dialogueLines.push({ text }));
    }

    // Method 4: Ultimate fallback - split into sentences
    if (dialogueLines.length === 0) {
      const allText = generatedScript
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/[#\-\*]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const sentences = allText.split(/[。！？\.\!\?]+/).filter(s => s.trim().length > 5);
      sentences.forEach(text => dialogueLines.push({ text: text.trim() }));
    }

    return dialogueLines;
  }, [generatedScript]);

  // Handle video time update for subtitle display
  const handleVideoTimeUpdate = useCallback(() => {
    if (!recordedVideoRef.current || scriptSegments.length === 0 || videoDuration <= 0) {
      return;
    }

    const currentTime = recordedVideoRef.current.currentTime;
    const segmentDuration = videoDuration / scriptSegments.length;
    const currentIndex = Math.min(
      Math.floor(currentTime / segmentDuration),
      scriptSegments.length - 1
    );

    if (currentIndex >= 0 && currentIndex < scriptSegments.length) {
      setCurrentSubtitleText(scriptSegments[currentIndex].text);
    }
  }, [scriptSegments, videoDuration]);

  // Handle video loaded metadata
  const handleVideoLoadedMetadata = useCallback(() => {
    if (recordedVideoRef.current) {
      setVideoDuration(recordedVideoRef.current.duration);
    }
  }, []);

  // Handle platform selection
  const handlePlatformClick = (platformId) => {
    setSelectedPlatform(platformId);
    setCurrentStep(2);
  };

  // Handle video URL input
  const handleVideoUrlChange = (e) => {
    const value = e.target.value;
    setVideoUrl(value);
    setTranscriptionError(null);
    setTranscriptionText('');
  };

  // Transcribe video using Apify Transcriber API
  const transcribeVideo = async () => {
    // 驗證 URL 格式 - 支援 Instagram, TikTok, Facebook, YouTube
    const isValidUrl = videoUrl && platforms.some(p => p.pattern && p.pattern.test(videoUrl));

    if (!isValidUrl) {
      setTranscriptionError('請提供有效的影片連結 (Instagram Reels, TikTok, Facebook Reels)');
      return;
    }

    setIsTranscribing(true);
    setTranscriptionError(null);

    try {
      // 使用新的 Apify Transcriber API
      const response = await apiClient.post('/creator-to-script/transcribe', {
        url: videoUrl
      });

      if (response.data && response.data.success) {
        const data = response.data.data;
        // 使用純文字版本
        setTranscriptionText(data.plainText || '');
        setIsTranscribing(false);

        // 顯示成功訊息，包含影片資訊
        const platform = platforms.find(p => p.pattern && p.pattern.test(videoUrl));
        const platformName = platform ? platform.name : '影片';
        const duration = data.durationSec ? `${Math.round(data.durationSec)}秒` : '';
        const language = data.detectedLanguage || '';

        toast.success(`${platformName}轉錄完成！${duration ? `(${duration})` : ''} ${language ? `語言: ${language}` : ''}`);
      } else {
        setTranscriptionError(response.data?.error || '轉錄失敗');
        setIsTranscribing(false);
      }
    } catch (error) {
      console.error('轉錄失敗:', error);
      setTranscriptionError(error.response?.data?.error || '轉錄失敗，請稍後再試');
      setIsTranscribing(false);
    }
  };

  // Get similarity description
  const getSimilarityDescription = (value) => {
    if (value >= 80) return 'Almost completely mimic creator style';
    if (value >= 60) return 'High reference to creator features';
    if (value >= 40) return 'Partially integrate creator elements';
    if (value >= 20) return 'Light reference, maintain originality';
    return 'Completely original, reference topic only';
  };

  // Generate script
  const generateScript = async () => {
    if (!topic.trim()) {
      toast.warning('Please enter a topic');
      return;
    }

    setIsGenerating(true);

    try {
      const platformName = platforms.find(p => p.id === selectedPlatform)?.name || selectedPlatform;

      const referenceContent = transcriptionText ?
        `\n\n【參考影片轉錄內容】\n${transcriptionText}\n\n請分析上述轉錄內容的風格、語氣、表達方式,並據此創作腳本。` :
        '';

      const getSimilarityInstructions = (simValue) => {
        if (simValue >= 80) {
          return `- 極度模仿參考影片的風格：說話方式、用詞習慣、表達節奏都要高度相似
- 研究影片中的標誌性用語、口頭禪、語氣特色
- 模仿影片的結構和敘事方式`;
        } else if (simValue >= 60) {
          return `- 深度參考影片的創作風格
- 學習影片的語調、表達方式和內容節奏
- 保持影片的核心風格特色,但允許適度調整`;
        } else if (simValue >= 40) {
          return `- 部分融合影片的風格元素
- 參考影片的內容架構和呈現方式
- 在保持原創性的同時借鑒影片的優點`;
        } else {
          return `- 輕度參考影片的風格
- 了解影片的內容方向和主題選擇
- 主要保持原創性,僅在主題選擇上受啟發`;
        }
      };

      const getDurationStructure = (length) => {
        switch(length) {
          case '15s':
            return `【15秒腳本結構】
0-3秒：爆炸性開場
4-10秒：核心內容
11-15秒：行動呼籲`;
          case '30s':
            return `【30秒腳本結構】
0-3秒：鉤子開場
4-12秒：問題/情境設定
13-25秒：解決方案/核心內容
26-30秒：行動呼籲`;
          case '60s':
            return `【60秒腳本結構】
0-3秒：強力鉤子
4-10秒：問題引入
11-30秒：故事展開/教學內容
31-50秒：高潮/關鍵訊息
51-60秒：總結與行動呼籲`;
          case '90s':
            return `【90秒腳本結構】
0-3秒：震撼開場
4-15秒：背景設定
16-40秒：內容展開
41-70秒：深度內容/轉折
71-85秒：總結重點
86-90秒：強力CTA`;
          default:
            return '';
        }
      };

      const prompt = `你是一位專業的短影音腳本創作者,專精於 ${platformName} 的內容創作。

【任務】
基於參考影片的風格,撰寫一個 ${scriptLength} 的 ${platformName} 短影音腳本。

【主題】
${topic}

【影片連結】
${videoUrl}
${referenceContent}

【風格相似度】${similarity}%
${getSimilarityInstructions(similarity)}

${getDurationStructure(scriptLength)}

請直接輸出完整腳本,包含時間軸、台詞、視覺元素和拍攝建議。`;

      const systemPrompt = `你是一位資深的短影音腳本創作專家,擅長分析創作者風格並創作出高互動率的 ${platformName} 內容。`;

      const response = await apiClient.post('/ai/generate-post', {
        userMessage: prompt,
        systemPrompt: systemPrompt,
        accountId: selectedAccount?.id
      });

      if (response.data && response.data.content) {
        setGeneratedScript(response.data.content);
        toast.success('Script generated successfully!');
      }
    } catch (error) {
      console.error('生成腳本失敗:', error);
      toast.error('Failed to generate script');
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle recording completion
  const handleRecordingComplete = (videoData) => {
    console.log('Recording completed:', videoData);
    setRecordedVideo(videoData);
    setCurrentStep(3); // Return to script page
    toast.success('影片錄製完成！');
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      {/* Back Button */}
      <BackButton
        onClick={() => navigate('/templates/creator-to-script')}
        className="fixed top-8 left-8"
      >
        Back
      </BackButton>

      {/* Main Content Box */}
      <div className="max-w-[980px] w-full bg-white p-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-[2.5rem] font-bold text-black mb-2">Creator to Script</h1>
          <p className="text-base text-gray-500">Reference creator style and generate short video scripts</p>
        </div>

        {/* Step 1: Platform Selection */}
        {currentStep === 1 && (
          <div className="mb-8">
            <div className="text-sm font-semibold text-slate-800 mb-6 text-center">
              Select Video Platform
            </div>
            <div className="flex gap-6 justify-center flex-wrap">
              {platforms.map((platform) => (
                <PlatformCard
                  key={platform.id}
                  icon={platform.icon}
                  iconColor={platform.color}
                  name={platform.name}
                  description={platform.description}
                  selected={selectedPlatform === platform.id}
                  onClick={() => {
                    setSelectedPlatform(platform.id);
                    setCurrentStep(2);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Video URL Input */}
        {currentStep === 2 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold text-slate-800">
                輸入 {selectedPlatform === 'instagram' ? 'Instagram Reel' : selectedPlatform === 'tiktok' ? 'TikTok' : selectedPlatform === 'facebook' ? 'Facebook Reel' : selectedPlatform === 'youtube' ? 'YouTube' : '影片'} 連結
              </div>
              <Button
                onClick={() => setCurrentStep(1)}
                variant="secondary"
                size="sm"
                icon="mdi:swap-horizontal"
              >
                Change Platform
              </Button>
            </div>
            <div className="flex gap-3 mb-6">
              <Input
                size="large"
                placeholder={
                  selectedPlatform === 'instagram' ? 'https://www.instagram.com/reel/...' :
                  selectedPlatform === 'tiktok' ? 'https://www.tiktok.com/@user/video/...' :
                  selectedPlatform === 'facebook' ? 'https://www.facebook.com/reel/... 或 /watch/...' :
                  selectedPlatform === 'youtube' ? 'https://youtube.com/shorts/... 或 https://youtu.be/...' :
                  '貼上影片連結'
                }
                value={videoUrl}
                onChange={handleVideoUrlChange}
                disabled={isTranscribing}
                prefix={<Icon icon="mdi:link" className="text-slate-400 text-xl" />}
                className="flex-1 rounded-lg"
              />
              <LoadingButton
                onClick={transcribeVideo}
                disabled={!videoUrl.trim()}
                loading={isTranscribing}
                loadingText="Transcribing..."
                icon="mdi:text-recognition"
                variant="primary"
                size="lg"
              >
                Transcribe
              </LoadingButton>
            </div>

            {/* Transcription Status */}
            {isTranscribing && (
              <div className="py-3 px-3 bg-blue-50 rounded-lg flex items-center gap-2 mb-4">
                <InlineSpinner size="sm" color="primary" />
                <span className="text-xs text-blue-800">
                  Transcribing video...
                </span>
              </div>
            )}

            {/* Transcription Error */}
            {transcriptionError && (
              <div className="py-3 px-3 bg-red-50 border border-red-300 rounded-lg flex items-center gap-2 mb-4">
                <Icon icon="mdi:alert-circle" className="text-red-500 text-base" />
                <span className="text-xs text-red-800">
                  {transcriptionError}
                </span>
              </div>
            )}

            {/* Transcription Success */}
            {transcriptionText && (
              <div className="p-6 bg-green-50 border border-green-300 rounded-xl mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Icon icon="mdi:check-circle" className="text-green-500 text-2xl" />
                    <span className="text-base text-green-800 font-semibold">
                      轉錄完成
                    </span>
                  </div>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(transcriptionText);
                      toast.success('轉錄內容已複製！');
                    }}
                    variant="secondary"
                    size="sm"
                    icon="mdi:content-copy"
                    className="!bg-white !border-green-300 !text-green-800 hover:!bg-green-100"
                  >
                    複製
                  </Button>
                </div>
                <div className="text-sm text-green-700 leading-relaxed max-h-[300px] overflow-y-auto p-4 bg-white rounded-lg whitespace-pre-wrap">
                  {transcriptionText}
                </div>
                <div className="mt-3 text-xs text-green-800 flex items-center gap-2">
                  <Icon icon="mdi:text" className="text-base" />
                  <span>字數：{transcriptionText.length} 字</span>
                </div>
              </div>
            )}

            <div className="text-center mt-8">
              <Button
                onClick={() => setCurrentStep(3)}
                variant="primary"
                size="lg"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Configuration & Generate */}
        {currentStep >= 3 && (
          <>
            {/* Configuration Controls */}
            <div className="grid grid-cols-4 gap-4 mb-8">
              <Dropdown
                value={scriptLength}
                onChange={(value) => setScriptLength(value)}
                size="lg"
                fullWidth
                options={scriptLengthOptions.map(opt => ({ value: opt.value, label: opt.label }))}
              />
              <Dropdown
                value={selectedPreset}
                onChange={(value) => setSelectedPreset(value)}
                size="lg"
                fullWidth
                options={[
                  { value: 'default', label: 'Default' },
                  { value: 'professional', label: 'Professional' },
                  { value: 'casual', label: 'Casual' }
                ]}
              />
              <Dropdown
                value={selectedLanguage}
                onChange={(value) => setSelectedLanguage(value)}
                size="lg"
                fullWidth
                options={[
                  { value: 'zh-TW', label: 'Traditional Chinese' },
                  { value: 'en-US', label: 'English' },
                  { value: 'ja-JP', label: 'Japanese' }
                ]}
              />
              <div className="py-2 px-4 bg-gray-100 rounded-lg flex items-center justify-center text-xs font-semibold text-slate-500">
                Similarity: {similarity}%
              </div>
            </div>

            {/* Similarity Slider */}
            <div className="p-6 bg-gray-50 rounded-xl mb-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-xl font-bold text-blue-500">
                  {similarity}%
                </span>
                <span className="text-[10px] text-slate-500 italic">
                  {getSimilarityDescription(similarity)}
                </span>
              </div>

              <Slider
                min={0}
                max={100}
                value={similarity}
                onChange={(value) => setSimilarity(value)}
                trackStyle={{ background: '#3b82f6', height: '6px' }}
                railStyle={{ height: '6px', background: '#e5e7eb' }}
              />

              <div className="flex justify-between mt-2">
                <span className="text-[10px] text-slate-400">Original</span>
                <span className="text-[10px] text-slate-400">Mimic</span>
              </div>
            </div>

            {/* Gemini-style Text Input */}
            <div className="mb-8">
              <div className="flex items-center gap-4 py-4 px-6 bg-gray-100 rounded-[28px] transition-all duration-200 border border-gray-200">
                <button className="p-2 bg-transparent text-slate-500 border-none cursor-pointer flex items-center justify-center min-w-[24px] min-h-[24px]">
                  <Icon icon="mdi:plus" className="text-2xl" />
                </button>
                <Input.TextArea
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Describe the topic or style you want to create..."
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  className="flex-1 text-base border-none p-0 resize-none shadow-none bg-transparent text-slate-800"
                  style={{ boxShadow: 'none' }}
                />
                <button
                  onClick={toggleRecording}
                  className={`p-2 border-none rounded-full cursor-pointer flex items-center justify-center min-w-[24px] min-h-[24px] transition-all duration-200 ${
                    isRecording
                      ? 'bg-red-500 text-white'
                      : 'bg-transparent text-slate-500'
                  }`}
                >
                  <Icon icon="mdi:microphone" className="text-2xl" />
                </button>
                <button
                  onClick={generateScript}
                  disabled={!topic.trim() || isGenerating}
                  className={`p-2 bg-transparent border-none flex items-center justify-center min-w-[24px] min-h-[24px] transition-all duration-200 ${
                    !topic.trim() || isGenerating
                      ? 'text-slate-300 cursor-not-allowed'
                      : 'text-slate-500 cursor-pointer hover:text-black'
                  }`}
                >
                  <Icon icon={isGenerating ? "mdi:loading" : "mdi:send"} className={`text-2xl ${isGenerating ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Generate Button */}
            <div className="flex gap-4">
              <LoadingButton
                onClick={generateScript}
                disabled={!topic.trim()}
                loading={isGenerating}
                loadingText="Generating..."
                icon="mdi:script-text"
                variant="primary"
                size="lg"
                className="flex-1"
              >
                Generate Script
              </LoadingButton>
            </div>

            {/* Generated Script Display */}
            {generatedScript && currentStep === 3 && (
              <div className="mt-8 p-8 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="m-0 text-base font-semibold text-slate-800">
                    Generated Script
                  </h3>
                  <div className="flex gap-2">
                    {isEditingScript ? (
                      <Button
                        onClick={() => {
                          setIsEditingScript(false);
                          toast.success('Script saved!');
                        }}
                        variant="accent"
                        size="sm"
                        icon="mdi:check"
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setIsEditingScript(true)}
                        variant="secondary"
                        size="sm"
                        icon="mdi:pencil"
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(generatedScript);
                        toast.success('Script copied to clipboard!');
                      }}
                      variant="secondary"
                      size="sm"
                      icon="mdi:content-copy"
                    >
                      Copy
                    </Button>
                  </div>
                </div>
                {isEditingScript ? (
                  <Input.TextArea
                    value={generatedScript}
                    onChange={(e) => setGeneratedScript(e.target.value)}
                    autoSize={{ minRows: 10, maxRows: 20 }}
                    className="text-xs text-slate-800 leading-relaxed font-mono bg-white p-6 rounded-lg border-2 border-blue-500"
                  />
                ) : (
                  <div className="text-xs text-slate-800 leading-relaxed whitespace-pre-wrap font-mono bg-white p-6 rounded-lg max-h-[500px] overflow-y-auto">
                    {generatedScript}
                  </div>
                )}

                {/* Recorded Video Display with Subtitle Overlay */}
                {recordedVideo && (
                  <div className="mt-8 p-6 bg-white rounded-xl border-2 border-emerald-500">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon icon="mdi:check-circle" className="text-2xl text-emerald-500" />
                      <h3 className="m-0 text-base font-semibold text-emerald-600">
                        錄製完成的影片
                      </h3>
                    </div>
                    <div className="flex justify-center items-center">
                      {/* Video Container with Subtitle Overlay */}
                      <div className="relative w-full max-w-[360px]">
                        <video
                          ref={recordedVideoRef}
                          src={recordedVideo.videoUrl}
                          controls
                          onTimeUpdate={handleVideoTimeUpdate}
                          onLoadedMetadata={handleVideoLoadedMetadata}
                          className="w-full aspect-[9/16] rounded-xl shadow-lg object-contain"
                        />
                        {/* Subtitle Overlay */}
                        {currentSubtitleText && (
                          <div className="absolute bottom-[60px] left-1/2 -translate-x-1/2 w-[90%] text-center pointer-events-none z-10">
                            <span className="inline-block bg-black/75 text-white py-2 px-4 rounded-lg text-sm font-semibold leading-snug max-w-full break-words shadow-[1px_1px_2px_rgba(0,0,0,0.5)]">
                              {currentSubtitleText}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Next Step Button */}
                {!recordedVideo && (
                  <div className="text-center mt-8">
                    <Button
                      onClick={() => setCurrentStep(4)}
                      variant="primary"
                      size="lg"
                      iconRight="mdi:arrow-right"
                    >
                      開始錄製影片
                    </Button>
                  </div>
                )}

                {/* Action Buttons for Recorded Video */}
                {recordedVideo && (
                  <div className="flex gap-4 flex-wrap justify-center mt-6">
                    {/* Re-record Button */}
                    <Button
                      onClick={() => {
                        setRecordedVideo(null);
                        setCurrentStep(4);
                      }}
                      variant="secondary"
                      size="lg"
                      icon="mdi:reload"
                    >
                      重新錄製
                    </Button>

                    {/* Download Button */}
                    <a
                      href={recordedVideo.videoUrl}
                      download={`viralarc-video-${Date.now()}.webm`}
                      className="py-4 px-8 bg-emerald-500 text-white border-none rounded-lg text-base font-semibold cursor-pointer transition-all duration-200 inline-flex items-center gap-2 no-underline hover:bg-emerald-600 hover:-translate-y-0.5"
                    >
                      <Icon icon="mdi:download" className="text-lg" />
                      下載影片
                    </a>

                    {/* Back to Home Button */}
                    <Button
                      onClick={() => navigate('/home')}
                      variant="primary"
                      size="md"
                      icon="mdi:home"
                    >
                      Home
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Step 4: Recording Mode */}
        {currentStep === 4 && generatedScript && (
          <div className="w-full h-full fixed inset-0 bg-black z-[9999]">
            {/* Back Button */}
            <BackButton
              onClick={() => setCurrentStep(3)}
              className="fixed top-4 left-4 z-[10000] !bg-black/70 backdrop-blur-[10px] !border-white/10 !text-white hover:!bg-black/90"
            >
              返回腳本
            </BackButton>

            {/* PreviewTabs Component */}
            <PreviewTabs
              content={generatedScript}
              accountName={selectedAccount?.accountName || 'Your Account'}
              hasContent={true}
              onRecordingComplete={handleRecordingComplete}
              isMobileRecordMode={true}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default CreatorToScriptPage;
